import { supabase } from './supabaseService';
import { serviceConnectionsService } from './serviceConnectionsService';
import { logger } from '../utils/logger';

// Configuration Service for managing system settings
export interface SystemConfig {
  // Resend configuration
  resend: {
    apiKey: string;
    isConfigured: boolean;
    isActive?: boolean;
    fromEmail: string;
    fromName: string;
    lastSync?: Date;
  };
  // Firebase configuration
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    vapidKey: string;
    isActive?: boolean;
  };
  google: {
    clientId: string;
    clientSecret: string;
    redirectUri: string;
    isActive?: boolean;
  };
  sendgrid: {
    apiKey: string;
    isActive?: boolean;
  };
  openai: {
    apiKey: string;
    organizationId: string;
    model: string;
    maxTokens: number;
    temperature: number;
    isActive?: boolean;
  };
  general: {
    companyName: string;
    supportEmail: string;
    systemTimezone: string;
  };
}

class ConfigService {
  private config: SystemConfig | null = null;
  private migrationCompleted: boolean = false;
  private loadPromise: Promise<SystemConfig | null> | null = null;

  // Main entry point: Load configuration with database priority
  async loadConfig(forceReload: boolean = false): Promise<SystemConfig | null> {
    // Return cached config if already loaded (unless force reload)
    if (this.config && !forceReload) {
      logger.info('Usando configuración en caché');
      return this.config;
    }

    // Clear cache if force reload
    if (forceReload) {
      logger.debug('Forzando recarga de configuración...');
      this.config = null;
      this.loadPromise = null;
    }

    // If already loading, return the same promise
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start new load operation
    this.loadPromise = this.performLoad();
    const result = await this.loadPromise;
    this.loadPromise = null;
    return result;
  }

  // Clear cache and force reload from database
  clearCache(): void {
    logger.debug('Limpiando caché de configuración...');
    this.config = null;
    this.loadPromise = null;
  }

  // Perform the actual load operation
  private async performLoad(): Promise<SystemConfig | null> {
    try {
      logger.debug('Iniciando carga de configuración...');

      // Step 1: Try to load from database first (priority)
      const dbConfig = await this.loadFromDatabase();

      if (dbConfig && this.isConfigComplete(dbConfig)) {
        logger.info('Configuración cargada desde base de datos');
        this.config = dbConfig;

        // Check if we should clean localStorage after successful DB load
        await this.maybeCleanLocalStorage();

        return dbConfig;
      }

      // Step 2: If database is empty, check localStorage for migration
      logger.info('No hay configuración completa en BD, verificando localStorage...');
      const localConfig = this.loadFromLocalStorageSync();

      if (localConfig && this.isConfigComplete(localConfig)) {
        logger.debug('Migrando configuración desde localStorage a BD...');

        // Migrate silently to database
        const migrated = await this.migrateToDatabase(localConfig);

        if (migrated) {
          logger.info('Migración completada exitosamente');
          this.migrationCompleted = true;
          this.config = localConfig;

          // Mark for cleanup after verification period
          this.scheduleLocalStorageCleanup();

          return localConfig;
        }
      }

      return null;

    } catch (error) {
      logger.error('Error en performLoad', error);

      // Last resort fallback to localStorage
      const fallback = this.loadFromLocalStorageSync();
      if (fallback) {
        return fallback;
      }

      return null;
    }
  }

  // Check if configuration is complete enough to use
  private isConfigComplete(config: SystemConfig | Partial<SystemConfig>): boolean {
    // At minimum, we need general config
    if (!config.general) {
      return false;
    }

    // Check if at least one service is configured
    const hasAnyService =
      (config.resend && config.resend.apiKey) ||
      (config.firebase && config.firebase.apiKey) ||
      (config.google && config.google.clientId) ||
      (config.openai && config.openai.apiKey) ||
      (config.sendgrid && config.sendgrid.apiKey);

    return !!hasAnyService;
  }

  // Synchronous load from localStorage (for fallback only)
  private loadFromLocalStorageSync(): SystemConfig | null {
    try {
      const savedConfig = localStorage.getItem('crm_configuration');
      if (savedConfig) {
        const parsed = JSON.parse(savedConfig);
        return this.transformLegacyConfig(parsed);
      }
    } catch (error) {
      logger.error('Error loading from localStorage', error);
      this.clearCorruptedConfig();
    }
    return null;
  }

  // Clear corrupted configuration data
  private clearCorruptedConfig(): void {
    try {
      logger.debug('Limpiando configuración corrupta de localStorage...');
      localStorage.removeItem('crm_configuration');
      localStorage.removeItem('firebase_config');
      localStorage.removeItem('google_config');
      localStorage.removeItem('openai_config');
      localStorage.removeItem('sendgrid_config');
      localStorage.removeItem('general_config');
      logger.info('Configuración corrupta limpiada');
    } catch (error) {
      logger.error('Error limpiando configuración corrupta', error);
    }
  }

  // Clear all localStorage data (for debugging)
  clearAllLocalStorage(): void {
    try {
      logger.debug('Limpiando to do el localStorage...');
      
      // Configuration keys
      const configKeys = [
        'crm_configuration',
        'firebase_config', 
        'google_config',
        'openai_config',
        'sendgrid_config',
        'general_config'
      ];
      
      // Cache and temporary keys
      const cacheKeys = [
        'ai_insights_cache',
        'ai_usage_history',
        'connection_status_cache'
      ];
      
      // User preference keys
      const userKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('user_availability_') || 
        key.startsWith('user_preferences_')
      );
      
      const allKeys = [...configKeys, ...cacheKeys, ...userKeys];
      
      allKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      logger.info('Todo el localStorage ha sido limpiado');
      
      // Reset internal config
      this.config = null;
      
    } catch (error) {
      logger.error('Error limpiando localStorage', error);
    }
  }

  // Force reload configuration from database
  forceReloadFromDatabase = async (): Promise<SystemConfig | null> => {
    try {
      logger.debug('Forzando recarga de configuración desde base de datos...');

      // Clear cache
      this.config = null;
      this.loadPromise = null;
      serviceConnectionsService.clearAllCache();

      // Try to load from database
      const dbConfig = await this.loadFromDatabase();

      if (dbConfig) {
        logger.info('Configuración recargada desde base de datos');
        this.config = dbConfig;
        return dbConfig;
      } else {
        return null;
      }

    } catch (error) {
      logger.error('Error forzando recarga desde base de datos', error);
      return null;
    }
  };

  // Migrate configuration to database silently
  private async migrateToDatabase(config: SystemConfig): Promise<boolean> {
    try {
      logger.debug('Iniciando migración silenciosa a base de datos...');

      const services = [
        { name: 'firebase' as const, data: config.firebase },
        { name: 'google' as const, data: config.google },
        { name: 'openai' as const, data: config.openai },
        { name: 'sendgrid' as const, data: config.sendgrid }
      ];

      let successCount = 0;
      let failCount = 0;

      for (const service of services) {
        if (!service.data) continue;

        try {
          let apiKey: string | undefined;
          let configData: Record<string, any> = {};

          switch (service.name) {
            case 'firebase':
              apiKey = service.data.apiKey;
              configData = {
                authDomain: service.data.authDomain,
                projectId: service.data.projectId,
                storageBucket: service.data.storageBucket,
                messagingSenderId: service.data.messagingSenderId,
                appId: service.data.appId,
                vapidKey: service.data.vapidKey
              };
              break;
            case 'google':
              apiKey = service.data.clientSecret;
              configData = {
                clientId: service.data.clientId,
                redirectUri: service.data.redirectUri
              };
              break;
            case 'openai':
              apiKey = service.data.apiKey;
              configData = {
                organizationId: service.data.organizationId,
                model: service.data.model,
                maxTokens: service.data.maxTokens,
                temperature: service.data.temperature
              };
              break;
            case 'sendgrid':
              apiKey = service.data.apiKey;
              break;
          }

          if (apiKey || Object.keys(configData).length > 0) {
            await serviceConnectionsService.saveServiceConnection(
              service.name,
              apiKey,
              configData
            );
            successCount++;
            logger.info(`${service.name} migrado exitosamente`);
          }
        } catch (error) {
          logger.error(`Error migrando ${service.name}:`, error);
          failCount++;
        }
      }

      logger.debug(`Migración completada: ${successCount} exitosos, ${failCount} fallidos`);

      // Consider migration successful if at least one service was migrated
      return successCount > 0;

    } catch (error) {
      logger.error('Error en migración a base de datos', error);
      return false;
    }
  }

  // Schedule localStorage cleanup after verification period
  private scheduleLocalStorageCleanup(): void {
    try {
      const cleanupDate = new Date();
      cleanupDate.setDate(cleanupDate.getDate() + 7); // 7 days from now

      localStorage.setItem('crm_config_cleanup_scheduled', cleanupDate.toISOString());
    } catch (error) {
    }
  }

  // Check if it's time to clean localStorage
  private async maybeCleanLocalStorage(): Promise<void> {
    try {
      const scheduledDate = localStorage.getItem('crm_config_cleanup_scheduled');

      if (!scheduledDate) {
        return; // No cleanup scheduled
      }

      const cleanupTime = new Date(scheduledDate);
      const now = new Date();

      if (now >= cleanupTime) {
        logger.debug('Es hora de limpiar localStorage...');

        // Verify database has the data before cleaning
        const dbConfig = await this.loadFromDatabase();

        if (dbConfig && this.isConfigComplete(dbConfig)) {
          logger.info('Base de datos verificada, limpiando localStorage...');
          this.clearAllLocalStorage();
          localStorage.removeItem('crm_config_cleanup_scheduled');
          logger.info('localStorage limpiado exitosamente');
        } else {
          cleanupTime.setDate(cleanupTime.getDate() + 1);
          localStorage.setItem('crm_config_cleanup_scheduled', cleanupTime.toISOString());
        }
      }
    } catch (error) {
    }
  }

  // Load configuration from database
  private loadFromDatabase = async (): Promise<SystemConfig | null> => {
    try {
      logger.debug('Cargando configuración desde base de datos...');

      // Load all service connections from database
      const services = await serviceConnectionsService.getAllServiceConnections();

      if (services.length === 0) {
        logger.info('No hay configuraciones en base de datos');
        return null;
      }

      // Build SystemConfig from service connections
      const config: Partial<SystemConfig> = {
        general: {
          companyName: 'Cero Ideas',
          supportEmail: 'contacto@ceroideas.es',
          systemTimezone: 'Europe/Madrid'
        }
      };

      for (const service of services) {
        switch (service.serviceName) {
          case 'firebase':
            config.firebase = {
              apiKey: service.apiKey || '',
              authDomain: service.configData.authDomain || '',
              projectId: service.configData.projectId || '',
              storageBucket: service.configData.storageBucket || '',
              messagingSenderId: service.configData.messagingSenderId || '',
              appId: service.configData.appId || '',
              vapidKey: service.configData.vapidKey || '',
              isActive: service.isActive
            };
            break;
          case 'google':
            config.google = {
              clientId: service.configData.clientId || '',
              clientSecret: service.apiKey || '',
              redirectUri: service.configData.redirectUri || '',
              isActive: service.isActive
            };
            break;
          case 'sendgrid':
            config.sendgrid = {
              apiKey: service.apiKey || '',
              isActive: service.isActive
            };
            break;
          case 'resend':
            config.resend = {
              apiKey: service.apiKey || '',
              isConfigured: service.isActive,
              isActive: service.isActive,
              fromEmail: service.configData.fromEmail || '',
              fromName: service.configData.fromName || 'Cero Ideas CRM',
              lastSync: service.lastConnectedAt
            };
            break;
          case 'openai':
            config.openai = {
              apiKey: service.apiKey || '',
              organizationId: service.configData.organizationId || '',
              model: service.configData.model || 'gpt-4',
              maxTokens: service.configData.maxTokens || 1000,
              temperature: service.configData.temperature || 0.7,
              isActive: service.isActive
            };
            break;
        }
      }

      logger.info('Configuración cargada desde base de datos');
      return config as SystemConfig;
    } catch (error) {
      logger.error('Error cargando desde base de datos', error);
      return null;
    }
  };

  // Load configuration async (public API)
  loadConfigAsync = async (): Promise<SystemConfig | null> => {
    return this.loadConfig();
  }

  // Transform legacy localStorage config to new format
  private transformLegacyConfig(legacyConfig: any): SystemConfig {
    return {
      resend: {
        apiKey: legacyConfig.resendApiKey || '',
        isConfigured: legacyConfig.resendIsConfigured || false,
        fromEmail: legacyConfig.resendFromEmail || '',
        fromName: legacyConfig.resendFromName || 'Cero Ideas CRM',
        lastSync: legacyConfig.resendLastSync ? new Date(legacyConfig.resendLastSync) : undefined
      },
      firebase: {
        apiKey: legacyConfig.firebaseApiKey || '',
        authDomain: legacyConfig.firebaseAuthDomain || '',
        projectId: legacyConfig.firebaseProjectId || '',
        storageBucket: legacyConfig.firebaseStorageBucket || '',
        messagingSenderId: legacyConfig.firebaseMessagingSenderId || '',
        appId: legacyConfig.firebaseAppId || '',
        vapidKey: legacyConfig.firebaseVapidKey || ''
      },
      google: {
        clientId: legacyConfig.googleClientId || '',
        clientSecret: legacyConfig.googleClientSecret || '',
        redirectUri: legacyConfig.googleRedirectUri || ''
      },
      sendgrid: {
        apiKey: legacyConfig.sendgridApiKey || ''
      },
      openai: {
        apiKey: legacyConfig.openaiApiKey || '',
        organizationId: legacyConfig.openaiOrganizationId || '',
        model: legacyConfig.openaiModel || 'gpt-4',
        maxTokens: legacyConfig.openaiMaxTokens || 1000,
        temperature: legacyConfig.openaiTemperature || 0.7
      },
      general: {
        companyName: legacyConfig.companyName || 'Cero Ideas',
        supportEmail: legacyConfig.supportEmail || 'contacto@ceroideas.es',
        systemTimezone: legacyConfig.systemTimezone || 'Europe/Madrid'
      }
    };
  }


  // Save configuration to database (NEW: database-first approach)
  saveConfigAsync = async (config: Partial<SystemConfig>): Promise<void> => {
    try {
      logger.debug('Guardando configuración en BD...');

      // Save each service to service_connections

      if (config.firebase) {
        await serviceConnectionsService.saveServiceConnection(
          'firebase',
          config.firebase.apiKey,
          {
            authDomain: config.firebase.authDomain,
            projectId: config.firebase.projectId,
            storageBucket: config.firebase.storageBucket,
            messagingSenderId: config.firebase.messagingSenderId,
            appId: config.firebase.appId,
            vapidKey: config.firebase.vapidKey
          },
          config.firebase.isActive !== false
        );
      }

      if (config.google) {
        await serviceConnectionsService.saveServiceConnection(
          'google',
          config.google.clientSecret,
          {
            clientId: config.google.clientId,
            redirectUri: config.google.redirectUri
          },
          config.google.isActive !== false
        );
      }

      if (config.openai) {
        await serviceConnectionsService.saveServiceConnection(
          'openai',
          config.openai.apiKey,
          {
            organizationId: config.openai.organizationId,
            model: config.openai.model,
            maxTokens: config.openai.maxTokens,
            temperature: config.openai.temperature
          },
          config.openai.isActive !== false
        );
      }

      if (config.sendgrid) {
        await serviceConnectionsService.saveServiceConnection(
          'sendgrid',
          config.sendgrid.apiKey,
          {},
          config.sendgrid.isActive !== false
        );
      }

      if (config.resend) {
        await serviceConnectionsService.saveServiceConnection(
          'resend',
          config.resend.apiKey,
          {
            fromEmail: config.resend.fromEmail,
            fromName: config.resend.fromName,
            isConfigured: config.resend.isConfigured
          },
          config.resend.isActive !== false
        );
      }

      // Clear cache to force reload from database
      this.clearCache();
      serviceConnectionsService.clearAllCache();

      // Reload configuration from database
      await this.loadConfig(true);

      logger.info('Configuración guardada y recargada desde BD');
    } catch (error) {
      logger.error('Error guardando configuración en BD', error);
      throw error;
    }
  };

  // Legacy method for backward compatibility - saves to localStorage and database
  saveConfig(config: Partial<SystemConfig>): void {
    try {
      // Load current legacy config
      const currentLegacyConfig = JSON.parse(localStorage.getItem('crm_configuration') || '{}');

      // Update with new values
      const updatedLegacyConfig = { ...currentLegacyConfig };

      if (config.firebase) {
        updatedLegacyConfig.firebaseApiKey = config.firebase.apiKey;
        updatedLegacyConfig.firebaseAuthDomain = config.firebase.authDomain;
        updatedLegacyConfig.firebaseProjectId = config.firebase.projectId;
        updatedLegacyConfig.firebaseStorageBucket = config.firebase.storageBucket;
        updatedLegacyConfig.firebaseMessagingSenderId = config.firebase.messagingSenderId;
        updatedLegacyConfig.firebaseAppId = config.firebase.appId;
        updatedLegacyConfig.firebaseVapidKey = config.firebase.vapidKey;
      }

      if (config.google) {
        updatedLegacyConfig.googleClientId = config.google.clientId;
        updatedLegacyConfig.googleClientSecret = config.google.clientSecret;
        updatedLegacyConfig.googleRedirectUri = config.google.redirectUri;
      }

      if (config.openai) {
        updatedLegacyConfig.openaiApiKey = config.openai.apiKey;
        updatedLegacyConfig.openaiOrganizationId = config.openai.organizationId;
        updatedLegacyConfig.openaiModel = config.openai.model;
        updatedLegacyConfig.openaiMaxTokens = config.openai.maxTokens;
        updatedLegacyConfig.openaiTemperature = config.openai.temperature;
      }

      if (config.sendgrid) {
        updatedLegacyConfig.sendgridApiKey = config.sendgrid.apiKey;
      }

      logger.debug('Guardando configuración en localStorage y BD...');

      localStorage.setItem('crm_configuration', JSON.stringify(updatedLegacyConfig));

      // Save to database (non-blocking)
      this.saveToDatabaseAsync(config, updatedLegacyConfig).catch(error => {
        logger.error('Error guardando en BD (background)', error);
      });
    } catch (error) {
      logger.error('Error en saveConfig', error);
      throw error;
    }
  }

  // Save to database using serviceConnectionsService
  private async saveToDatabaseAsync(config: Partial<SystemConfig>, legacyConfig: any): Promise<void> {
    try {
      logger.debug('Guardando configuraciones en service_connections...');

      // Save each service configuration

      if (config.firebase) {
        await serviceConnectionsService.saveServiceConnection(
          'firebase',
          config.firebase.apiKey,
          {
            authDomain: config.firebase.authDomain,
            projectId: config.firebase.projectId,
            storageBucket: config.firebase.storageBucket,
            messagingSenderId: config.firebase.messagingSenderId,
            appId: config.firebase.appId,
            vapidKey: config.firebase.vapidKey
          }
        );
      }

      if (config.google) {
        await serviceConnectionsService.saveServiceConnection(
          'google',
          config.google.clientSecret,
          {
            clientId: config.google.clientId,
            redirectUri: config.google.redirectUri
          }
        );
      }

      if (config.openai) {
        await serviceConnectionsService.saveServiceConnection(
          'openai',
          config.openai.apiKey,
          {
            organizationId: config.openai.organizationId,
            model: config.openai.model,
            maxTokens: config.openai.maxTokens,
            temperature: config.openai.temperature
          }
        );
      }

      if (config.sendgrid) {
        await serviceConnectionsService.saveServiceConnection(
          'sendgrid',
          config.sendgrid.apiKey,
          {}
        );
      }

      logger.info('Configuraciones guardadas en BD');
    } catch (error) {
      logger.error('Error en saveToDatabaseAsync', error);
      throw error;
    }
  }

  // Transform new config format back to legacy format for localStorage
  private transformToLegacyFormat(config: SystemConfig): any {
    return {
      firebaseApiKey: config.firebase?.apiKey || '',
      firebaseAuthDomain: config.firebase?.authDomain || '',
      firebaseProjectId: config.firebase?.projectId || '',
      firebaseStorageBucket: config.firebase?.storageBucket || '',
      firebaseMessagingSenderId: config.firebase?.messagingSenderId || '',
      firebaseAppId: config.firebase?.appId || '',
      firebaseVapidKey: config.firebase?.vapidKey || '',
      googleClientId: config.google?.clientId || '',
      googleClientSecret: config.google?.clientSecret || '',
      googleRedirectUri: config.google?.redirectUri || '',
      sendgridApiKey: config.sendgrid?.apiKey || '',
      openaiApiKey: config.openai?.apiKey || '',
      openaiOrganizationId: config.openai?.organizationId || '',
      openaiModel: config.openai?.model || 'gpt-4',
      openaiMaxTokens: config.openai?.maxTokens || 1000,
      openaiTemperature: config.openai?.temperature || 0.7,
      companyName: config.general?.companyName || 'Cero Ideas',
      supportEmail: config.general?.supportEmail || 'contacto@ceroideas.es',
      systemTimezone: config.general?.systemTimezone || 'Europe/Madrid'
    };
  }

  getFirebaseConfig() {
    // Try to return from cache first
    if (this.config?.firebase) {
      return this.config.firebase;
    }

    // Fallback to localStorage for sync access (temporary during migration)
    try {
      const savedConfig = localStorage.getItem('crm_configuration');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        if (config.firebaseApiKey && config.firebaseProjectId) {
          return {
            apiKey: config.firebaseApiKey,
            authDomain: config.firebaseAuthDomain || `${config.firebaseProjectId}.firebaseapp.com`,
            projectId: config.firebaseProjectId,
            storageBucket: config.firebaseStorageBucket || `${config.firebaseProjectId}.appspot.com`,
            messagingSenderId: config.firebaseMessagingSenderId || '',
            appId: config.firebaseAppId || '',
            vapidKey: config.firebaseVapidKey || ''
          };
        }
      }
    } catch (error) {
      logger.error('Error loading Firebase config', error);
    }

    return null;
  }

  getGoogleConfig() {
    // Try to return from cache first
    if (this.config?.google) {
      return this.config.google;
    }

    // Fallback to localStorage for sync access (temporary during migration)
    try {
      const legacyConfig = localStorage.getItem('crm_configuration');
      if (legacyConfig) {
        const parsed = JSON.parse(legacyConfig);
        if (parsed.googleClientId && parsed.googleClientSecret) {
          return {
            clientId: parsed.googleClientId,
            clientSecret: parsed.googleClientSecret,
            redirectUri: parsed.googleRedirectUri || `${window.location.origin}/auth/google/callback`
          };
        }
      }
    } catch (error) {
    }

    return null;
  }

  getOpenAIConfig() {
    // Try to return from cache first
    if (this.config?.openai) {
      return this.config.openai;
    }

    // If no cache, trigger async load from database
    this.loadConfig().then((config) => {
      if (config?.openai) {
        logger.info('OpenAI config loaded from database');
      }
    }).catch(() => {});

    // Fallback to localStorage for sync access (temporary during migration)
    try {
      const savedConfig = localStorage.getItem('crm_configuration');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        if (config.openaiApiKey && config.openaiApiKey.startsWith('sk-')) {
          return {
            apiKey: config.openaiApiKey,
            organizationId: config.openaiOrganizationId || '',
            model: config.openaiModel || 'gpt-4',
            maxTokens: config.openaiMaxTokens || 1000,
            temperature: config.openaiTemperature || 0.7
          };
        }
      }
    } catch (error) {
      logger.error('Error loading OpenAI config', error);
    }

    return null;
  }

  async getOpenAIConfigAsync() {
    const config = await this.loadConfig();
    return config?.openai || null;
  }

  getSendGridConfig() {
    // Try to return from cache first
    if (this.config?.sendgrid) {
      return this.config.sendgrid;
    }

    // Fallback to localStorage for sync access (temporary during migration)
    try {
      const legacyConfig = localStorage.getItem('crm_configuration');
      if (legacyConfig) {
        const parsed = JSON.parse(legacyConfig);
        if (parsed.sendgridApiKey) {
          return {
            apiKey: parsed.sendgridApiKey
          };
        }
      }
    } catch (error) {
    }

    return null;
  }

  getGeneralConfig() {
    // Try to return from cache first
    if (this.config?.general) {
      return this.config.general;
    }

    // Return defaults
    return {
      companyName: 'Cero Ideas',
      supportEmail: 'contacto@ceroideas.es',
      systemTimezone: 'Europe/Madrid'
    };
  }

  // Check if service is configured
  isFirebaseConfigured(): boolean {
    const config = this.getFirebaseConfig();
    logger.debug('Firebase isConfigured() - config obtenida', !!config ? 'VALID_CONFIG' : 'NULL');
    const isConfigured = !!(config?.apiKey && config?.projectId);
    logger.debug('Firebase configurado resultado', isConfigured);
    return isConfigured;
  }

  isGoogleConfigured(): boolean {
    const config = this.getGoogleConfig();
    const isConfigured = !!(config?.clientId && config?.clientSecret);
    logger.debug('Google configurado resultado', isConfigured, {
      hasClientId: !!config?.clientId,
      hasClientSecret: !!config?.clientSecret
    });
    return isConfigured;
  }

  isSendGridConfigured(): boolean {
    const config = this.getSendGridConfig();
    const isConfigured = !!(config?.apiKey && config.apiKey.startsWith('SG.'));
    return isConfigured;
  }

  isOpenAIConfigured(): boolean {
    const config = this.getOpenAIConfig();
    logger.debug('OpenAI isConfigured() - config obtenida', !!config ? 'VALID_CONFIG' : 'NULL');
    const isConfigured = !!(config?.apiKey && config.apiKey.startsWith('sk-'));
    logger.debug('OpenAI configurado resultado', isConfigured);
    return isConfigured;
  }

  getResendConfig() {
    if (this.config?.resend) {
      return this.config.resend;
    }

    try {
      const legacyConfig = localStorage.getItem('crm_configuration');
      if (legacyConfig) {
        const parsed = JSON.parse(legacyConfig);
        if (parsed.resendApiKey) {
          return {
            apiKey: parsed.resendApiKey,
            isConfigured: parsed.resendIsConfigured || false,
            fromEmail: parsed.resendFromEmail || '',
            fromName: parsed.resendFromName || 'Cero Ideas CRM'
          };
        }
      }
    } catch (error) {
    }

    return null;
  }

  isResendConfigured(): boolean {
    const config = this.getResendConfig();
    const isConfigured = !!(config?.apiKey && config.apiKey.startsWith('re_'));
    return isConfigured;
  }

  // Generate environment variables content
  generateEnvContent(): string {
    if (!this.config) return '';

    return `# Firebase Configuration
VITE_FIREBASE_API_KEY=${this.config.firebase?.apiKey || ''}
VITE_FIREBASE_AUTH_DOMAIN=${this.config.firebase?.authDomain || ''}
VITE_FIREBASE_PROJECT_ID=${this.config.firebase?.projectId || ''}
VITE_FIREBASE_STORAGE_BUCKET=${this.config.firebase?.storageBucket || ''}
VITE_FIREBASE_MESSAGING_SENDER_ID=${this.config.firebase?.messagingSenderId || ''}
VITE_FIREBASE_APP_ID=${this.config.firebase?.appId || ''}
VITE_FIREBASE_VAPID_KEY=${this.config.firebase?.vapidKey || ''}

# Google Calendar Integration
VITE_GOOGLE_CLIENT_ID=${this.config.google?.clientId || ''}
VITE_GOOGLE_CLIENT_SECRET=${this.config.google?.clientSecret || ''}
VITE_GOOGLE_REDIRECT_URI=${this.config.google?.redirectUri || 'http://localhost:5173/auth/google/callback'}

# SendGrid Email Service
SENDGRID_API_KEY=${this.config.sendgrid?.apiKey || ''}

# OpenAI Configuration
OPENAI_API_KEY=${this.config.openai?.apiKey || ''}
OPENAI_ORGANIZATION_ID=${this.config.openai?.organizationId || ''}
OPENAI_MODEL=${this.config.openai?.model || 'gpt-4'}
OPENAI_MAX_TOKENS=${this.config.openai?.maxTokens || 1000}
OPENAI_TEMPERATURE=${this.config.openai?.temperature || 0.7}

# General Configuration
COMPANY_NAME=${this.config.general?.companyName || 'Cero Ideas'}
SUPPORT_EMAIL=${this.config.general?.supportEmail || 'contacto@ceroideas.es'}
SYSTEM_TIMEZONE=${this.config.general?.systemTimezone || 'Europe/Madrid'}
`;
  };

  // Test connections (mock implementation)
  testFirebaseConnection = async (): Promise<boolean> => {
    const firebaseConfig = this.getFirebaseConfig();
    if (!firebaseConfig?.apiKey || !firebaseConfig?.projectId) {
      return false;
    }

    try {
      // Test basic Firebase connection
      const isConnected = await this.testFirebaseConnectionBasic();

      if (isConnected) {
        logger.info('Firebase conectado correctamente');
        return true;
      } else {
        return false;
      }
    } catch (error) {
      logger.error('Error probando Firebase', error);
      return false;
    }
  };

  testFirebaseConnectionBasic = async (): Promise<boolean> => {
    const firebaseConfig = this.getFirebaseConfig();
    if (!firebaseConfig?.apiKey || !firebaseConfig?.projectId) {
      return false;
    }

    try {
      // Validate API Key format
      const isValidApiKey = firebaseConfig.apiKey.startsWith('AIza') && firebaseConfig.apiKey.length > 30;

      // Validate Project ID format
      const isValidProjectId = /^[a-z0-9-]+$/.test(firebaseConfig.projectId);

      // Check if other required fields are present
      const hasRequiredFields = !!(
        firebaseConfig.authDomain ||
        firebaseConfig.storageBucket ||
        firebaseConfig.messagingSenderId ||
        firebaseConfig.appId
      );

      const success = isValidApiKey && isValidProjectId && hasRequiredFields;

      if (success) {
        logger.info('Credenciales Firebase válidas');
        return true;
      } else {
        return false;
      }
    } catch (error) {
      logger.error('Error validando Firebase', error);
      return false;
    }
  };

  testGoogleConnection = async (): Promise<boolean> => {
    const config = this.getGoogleConfig();
    logger.debug('Google config cargada', config);
    if (!config?.clientId || !config?.clientSecret) {
      return false;
    }

    try {
      // Validate Client ID format
      const isValidClientId = config.clientId.includes('.apps.googleusercontent.com');
      const isValidSecret = config.clientSecret.startsWith('GOCSPX-');
      const success = isValidClientId && isValidSecret;
      return success;
    } catch (error) {
      logger.error('Error probando Google', error);
      return false;
    }
  };

  testSendGridConnection = async (): Promise<boolean> => {
    const config = this.getSendGridConfig();
    if (!config?.apiKey || !config.apiKey.startsWith('SG.')) {
      return false;
    }

    try {
      // Test SendGrid API
      const response = await fetch('https://api.sendgrid.com/v3/user/profile', {
        headers: {
          'Authorization': `Bearer ${config.apiKey}`
        }
      });
      const success = response.status === 200;
      return success;
    } catch (error) {
      logger.error('Error probando SendGrid', error);
      return false;
    }
  };

  testOpenAIConnection = async (): Promise<boolean> => {
    const openaiConfig = this.getOpenAIConfig();
    if (!openaiConfig?.apiKey || !openaiConfig.apiKey.startsWith('sk-')) {
      return false;
    }

    try {
      logger.debug('Probando conexión con OpenAI...');

      // Test OpenAI API with a simple completion
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${openaiConfig.apiKey}`,
          'Content-Type': 'application/json',
          ...(openaiConfig.organizationId && { 'OpenAI-Organization': openaiConfig.organizationId })
        }
      });

      const success = response.status === 200;

      if (!success) {
        const errorData = await response.text();
      } else {
        logger.info('OpenAI conectado correctamente');
      }

      return success;
    } catch (error) {
      logger.error('Error probando OpenAI', error);
      return false;
    }
  };

  // Diagnostic function to compare localStorage vs database
  async diagnoseConfigurationStatus(): Promise<{
    localStorageExists: boolean;
    databaseExists: boolean;
    servicesInLocalStorage: string[];
    servicesInDatabase: string[];
    migrationRecommended: boolean;
    cleanupScheduled: boolean;
    cleanupDate: string | null;
  }> {
    try {
      logger.debug('Diagnosticando estado de configuración...');

      // Check localStorage
      const localStorageExists = !!localStorage.getItem('crm_configuration');
      const servicesInLocalStorage: string[] = [];

      if (localStorageExists) {
        try {
          const legacyConfig = JSON.parse(localStorage.getItem('crm_configuration') || '{}');
          if (legacyConfig.firebaseApiKey) servicesInLocalStorage.push('firebase');
          if (legacyConfig.googleClientId) servicesInLocalStorage.push('google');
          if (legacyConfig.openaiApiKey) servicesInLocalStorage.push('openai');
          if (legacyConfig.sendgridApiKey) servicesInLocalStorage.push('sendgrid');
        } catch (error) {
        }
      }

      // Check database
      const dbServices = await serviceConnectionsService.getAllServiceConnections();
      const databaseExists = dbServices.length > 0;
      const servicesInDatabase = dbServices.map(s => s.serviceName);

      // Check cleanup status
      const cleanupScheduled = !!localStorage.getItem('crm_config_cleanup_scheduled');
      const cleanupDate = localStorage.getItem('crm_config_cleanup_scheduled');

      // Determine if migration is recommended
      const migrationRecommended = localStorageExists && !databaseExists;

      const diagnosis = {
        localStorageExists,
        databaseExists,
        servicesInLocalStorage,
        servicesInDatabase,
        migrationRecommended,
        cleanupScheduled,
        cleanupDate
      };

      return diagnosis;

    } catch (error) {
      logger.error('Error en diagnoseConfigurationStatus', error);
      return {
        localStorageExists: false,
        databaseExists: false,
        servicesInLocalStorage: [],
        servicesInDatabase: [],
        migrationRecommended: false,
        cleanupScheduled: false,
        cleanupDate: null
      };
    }
  }

  // Manual trigger for localStorage cleanup (admin use)
  async forceCleanupLocalStorage(): Promise<boolean> {
    try {
      logger.debug('Forzando limpieza de localStorage...');

      // Verify database has configuration before cleaning
      const dbConfig = await this.loadFromDatabase();

      if (!dbConfig || !this.isConfigComplete(dbConfig)) {
        return false;
      }

      logger.info('Base de datos verificada, procediendo con limpieza...');
      this.clearAllLocalStorage();
      localStorage.removeItem('crm_config_cleanup_scheduled');

      logger.info('localStorage limpiado exitosamente');
      return true;

    } catch (error) {
      logger.error('Error en forceCleanupLocalStorage', error);
      return false;
    }
  }
}

export const configService = new ConfigService();